import { useEffect, useState, type ReactNode } from "react";
import {
  IconAlertTriangle,
  IconCar,
  IconCash,
  IconCheck,
  IconCircleCheck,
  IconCircleX,
  IconCreditCard,
  IconMapPin,
  IconPhone,
  IconPlayerSkipForward,
  IconSearch,
  IconShare,
  IconStarFilled,
  IconUser,
  IconWallet,
} from "@tabler/icons-react";
import { TripStatus } from "@safiri/shared";
import { useI18n, type MessageKey } from "../i18n";
import { formatClock, formatMoney, initials, telHref } from "../lib/format";
import {
  isHeadingToIntermediateStop,
  phaseTitle,
  phaseTone,
  statusTime,
  type Phase,
  type StopView,
  type Tone,
} from "../lib/trip";
import type { Connection } from "../hooks/useLiveTrip";
import type { TripData } from "../types/trip";

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/5 ${className}`}>
      {children}
    </section>
  );
}

function CardTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-neutral-500">
      {children}
    </h2>
  );
}

const toneStyles: Record<Tone, { badge: string; icon: string }> = {
  neutral: { badge: "bg-neutral-100 text-ink", icon: "text-ink" },
  progress: { badge: "bg-primary/10 text-primary", icon: "text-primary" },
  success: { badge: "bg-green-50 text-green-700", icon: "text-green-700" },
  danger: { badge: "bg-red-50 text-red-700", icon: "text-red-700" },
};

function PhaseIcon({ phase, className }: { phase: Phase; className: string }) {
  const props = { size: 26, className, "aria-hidden": true } as const;
  switch (phase) {
    case "finding":
      return <IconSearch {...props} />;
    case "toPickup":
    case "onTrip":
      return <IconCar {...props} />;
    case "atPickup":
    case "atStop":
      return <IconMapPin {...props} />;
    case "arrived":
    case "completed":
      return <IconCircleCheck {...props} />;
    case "cancelled":
    case "noDriver":
      return <IconCircleX {...props} />;
  }
}

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

function Freshness({ connection, lastUpdate }: { connection: Connection; lastUpdate: number | null }) {
  const { t } = useI18n();
  const now = useNow(5000);
  if (connection === "offline") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700">
        <IconAlertTriangle size={14} aria-hidden="true" />
        {t("offline")}
      </span>
    );
  }
  if (connection === "reconnecting") {
    return <span className="text-xs font-medium text-amber-700">{t("reconnecting")}</span>;
  }
  const seconds = lastUpdate ? Math.max(0, Math.round((now - lastUpdate) / 1000)) : null;
  const ago =
    seconds == null || seconds < 10
      ? t("justNow")
      : seconds < 60
        ? t("secondsAgo", { n: seconds })
        : t("minutesAgo", { n: Math.round(seconds / 60) });
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-neutral-600">
      <span className="relative flex h-2 w-2" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60 motion-reduce:hidden" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-green-600" />
      </span>
      <span className="font-medium text-green-700">{t("live")}</span>
      <span aria-hidden="true">·</span>
      {t("updatedAgo", { ago })}
    </span>
  );
}

const timelineSteps: { status: TripStatus; key: MessageKey }[] = [
  { status: TripStatus.ACCEPTED, key: "timelineAccepted" },
  { status: TripStatus.DRIVER_ARRIVED, key: "timelineArrived" },
  { status: TripStatus.IN_PROGRESS, key: "timelinePickedUp" },
  { status: TripStatus.DESTINATION_ARRIVED, key: "timelineDroppedOff" },
];

export function StatusCard({
  trip,
  phase,
  etaMinutes,
  connection,
  lastUpdate,
}: {
  trip: TripData;
  phase: Phase;
  etaMinutes: number | null;
  connection: Connection;
  lastUpdate: number | null;
}) {
  const { t, lang } = useI18n();
  const tone = toneStyles[phaseTone[phase]];
  const live = phase !== "completed" && phase !== "cancelled" && phase !== "noDriver";

  let subtitle: string = t(
    phase === "onTrip" && isHeadingToIntermediateStop(trip, phase)
      ? "phaseOnTripNextStop"
      : (`${phaseTitle[phase]}Sub` as MessageKey)
  );
  if (phase === "atPickup") {
    const at = formatClock(statusTime(trip, TripStatus.DRIVER_ARRIVED), lang);
    if (at) subtitle = `${subtitle} · ${t("since", { time: at })}`;
  }
  const showEta = etaMinutes != null && etaMinutes > 0 && (phase === "toPickup" || phase === "onTrip");

  const steps = timelineSteps
    .map((step) => ({ ...step, at: formatClock(statusTime(trip, step.status), lang) }))
    .filter((step) => step.at);

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {live ? <Freshness connection={connection} lastUpdate={lastUpdate} /> : <span />}
        {trip.tripRefNumber && (
          <span className="text-xs text-neutral-500">{t("tripRef", { ref: `T-${trip.tripRefNumber}` })}</span>
        )}
      </div>

      <div className="flex items-start gap-3">
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${tone.badge}`}>
          <PhaseIcon phase={phase} className={tone.icon} />
        </div>
        <div className="min-w-0 flex-1" aria-live="polite">
          <h1 className="text-xl font-bold leading-tight text-ink">{t(phaseTitle[phase])}</h1>
          <p className="mt-1 text-sm leading-snug text-neutral-600">{subtitle}</p>
        </div>
        {showEta && (
          <div className="shrink-0 rounded-xl bg-ink px-3 py-2 text-right text-white">
            <div className="text-[11px] leading-none opacity-80">{t("arrivesIn")}</div>
            <div className="mt-1 text-lg font-bold tabular-nums leading-none">
              {t("etaMinutes", { n: etaMinutes })}
            </div>
          </div>
        )}
      </div>

      {steps.length > 0 && (
        <ol className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t border-neutral-100 pt-3 text-xs text-neutral-600">
          {steps.map((step) => (
            <li key={step.status} className="inline-flex items-center gap-1">
              <IconCheck size={14} className="text-green-700" aria-hidden="true" />
              {t(step.key)} <span className="tabular-nums">{step.at}</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function CallButton({ phone, label, variant }: { phone?: string; label: string; variant: "solid" | "outline" }) {
  const href = telHref(phone);
  if (!href) return null;
  const styles =
    variant === "solid"
      ? "bg-ink text-white hover:bg-black"
      : "bg-white text-ink ring-1 ring-neutral-300 hover:bg-neutral-50";
  return (
    <a
      href={href}
      className={`inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-xl text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${styles}`}
    >
      <IconPhone size={18} aria-hidden="true" />
      {label}
    </a>
  );
}

export function DriverCard({ trip, showCall }: { trip: TripData; showCall: boolean }) {
  const { t } = useI18n();
  const driver = trip.driverDetail;
  if (!driver?.name) return null;
  const vehicle = driver.vehicleDetail;
  const vehicleLine = [vehicle?.color, vehicle?.make, vehicle?.model].filter(Boolean).join(" ");
  const plate = vehicle?.vehicleRegistrationNumber?.trim();

  return (
    <Card>
      <CardTitle>{t("driver")}</CardTitle>
      <div className="flex items-center gap-3">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-base font-bold text-primary"
          aria-hidden="true"
        >
          {initials(driver.name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold text-ink">{driver.name}</p>
          <p className="truncate text-sm text-neutral-600">{vehicleLine}</p>
        </div>
        {typeof driver.rating === "number" && driver.rating > 0 && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-1 text-sm font-semibold tabular-nums text-ink"
            aria-label={t("rating", { rating: driver.rating.toFixed(1) })}
          >
            <IconStarFilled size={14} className="text-amber-500" aria-hidden="true" />
            {driver.rating.toFixed(1)}
          </span>
        )}
      </div>

      {plate && (
        <div className="mt-4 rounded-xl bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
          <p className="font-mono text-2xl font-bold tracking-[0.18em] text-ink">{plate}</p>
          <p className="mt-0.5 text-xs text-amber-900">{t("plateHint")}</p>
        </div>
      )}

      {showCall && (
        <div className="mt-4">
          <CallButton phone={driver.phoneNumber} label={t("callDriver")} variant="solid" />
        </div>
      )}
    </Card>
  );
}

export function RiderCard({ trip, showCall }: { trip: TripData; showCall: boolean }) {
  const { t } = useI18n();
  const rider = trip.passengerDetail;
  if (!rider?.name) return null;
  return (
    <Card>
      <CardTitle>{t("rider")}</CardTitle>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-600" aria-hidden="true">
          <IconUser size={20} />
        </div>
        <p className="min-w-0 flex-1 truncate font-semibold text-ink">{rider.name}</p>
      </div>
      {showCall && rider.phoneNumber && (
        <div className="mt-4">
          <CallButton phone={rider.phoneNumber} label={t("callRider")} variant="outline" />
        </div>
      )}
    </Card>
  );
}

export function RouteCard({ stops }: { stops: StopView[] }) {
  const { t } = useI18n();
  if (stops.length === 0) return null;
  return (
    <Card>
      <CardTitle>{t("route")}</CardTitle>
      <ol className="relative">
        {stops.map((stop, i) => {
          const label =
            stop.kind === "pickup" ? t("pickup") : stop.kind === "dropoff" ? t("dropoff") : t("stopN", { n: stop.index });
          const dot =
            stop.kind === "pickup"
              ? "bg-green-600"
              : stop.kind === "dropoff"
                ? "bg-primary"
                : "bg-ink";
          const muted = stop.state === "done" || stop.state === "skipped";
          return (
            <li key={stop.index} className="relative flex gap-3 pb-4 last:pb-0">
              {i < stops.length - 1 && (
                <span className="absolute left-[7px] top-5 h-[calc(100%-12px)] w-px bg-neutral-200" aria-hidden="true" />
              )}
              <span className={`relative mt-1 h-[15px] w-[15px] shrink-0 rounded-full ring-4 ring-white ${dot} ${muted ? "opacity-40" : ""}`} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{label}</span>
                  {stop.state === "next" && (
                    <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-semibold text-white">{t("next")}</span>
                  )}
                  {stop.state === "done" && (
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-green-700">
                      <IconCheck size={12} aria-hidden="true" />
                      {t("done")}
                    </span>
                  )}
                  {stop.state === "skipped" && (
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-neutral-500">
                      <IconPlayerSkipForward size={12} aria-hidden="true" />
                      {t("skipped")}
                    </span>
                  )}
                </div>
                <p className={`mt-0.5 break-words text-sm leading-snug ${muted ? "text-neutral-500" : "text-ink"} ${stop.state === "skipped" ? "line-through" : ""}`}>
                  {stop.address}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

export function FareCard({ trip, phase }: { trip: TripData; phase: Phase }) {
  const { t } = useI18n();
  const final = phase === "arrived" || phase === "completed";
  const fare = final ? trip.paymentDetail?.pricingDetail : trip.rideTypeDetail?.rideFareDetails;
  if (fare?.netFare == null) return null;
  const mode = trip.paymentDetail?.paymentMode?.toLowerCase();
  const modeLabel = mode === "cash" ? t("cash") : mode === "online" ? t("online") : mode === "wallet" ? t("wallet") : null;
  const ModeIcon = mode === "cash" ? IconCash : mode === "online" ? IconCreditCard : IconWallet;

  return (
    <Card>
      <CardTitle>{t("fare")}</CardTitle>
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-sm text-neutral-600">{final ? t("totalFare") : t("estimatedFare")}</p>
          <p className="text-2xl font-bold tabular-nums text-ink">{formatMoney(fare.netFare)}</p>
        </div>
        {modeLabel && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1.5 text-sm font-medium text-ink">
            <ModeIcon size={16} aria-hidden="true" />
            {modeLabel}
          </span>
        )}
      </div>
      {!final && <p className="mt-2 text-xs text-neutral-500">{t("fareNote")}</p>}
    </Card>
  );
}

export function SafetyCard({ trip, live }: { trip: TripData; live: boolean }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const emergency = live ? trip.emergencyNumber?.trim() : null;
  const emergencyHref = telHref(emergency ?? undefined);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: t("liveTracking"), url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 3000);
    } catch {
      // The viewer dismissed the share sheet, or clipboard access was denied.
    }
  };

  return (
    <Card>
      <CardTitle>{t("safety")}</CardTitle>
      <div className="grid gap-3 sm:grid-cols-2">
        {emergencyHref && (
          <a
            href={emergencyHref}
            className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-red-700 text-sm font-semibold text-white transition-colors hover:bg-red-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700"
          >
            <IconAlertTriangle size={18} aria-hidden="true" />
            {t("emergency")} · {t("callEmergency", { number: emergency ?? "" })}
          </a>
        )}
        <button
          type="button"
          onClick={share}
          className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-white text-sm font-semibold text-ink ring-1 ring-neutral-300 transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          {copied ? <IconCheck size={18} aria-hidden="true" /> : <IconShare size={18} aria-hidden="true" />}
          <span aria-live="polite">{copied ? t("copied") : t("share")}</span>
        </button>
      </div>
    </Card>
  );
}
